import { logEvent } from "firebase/analytics";
import posthog from "posthog-js";
import slugify from "slugify";

import { getFirebase } from "../configs/firebase";
import { User } from "../store/pomeriumApi";

export const productAnalyticsEvents = {
  activationClusterTokenCopied: "activation_cluster_token_copied",
  activationInstallPathSelected: "activation_install_path_selected",
  activationOnboardingStarted: "activation_onboarding_started",
  authEmailVerified: "auth_email_verified",
  authSignupCompleted: "auth_signup_completed",
  billingUpgradeViewed: "billing_upgrade_viewed",
  mcpBannerDismissed: "mcp_banner_dismissed",
  mcpBannerLearnMoreClicked: "mcp_banner_learn_more_clicked",
  mcpBannerViewed: "mcp_banner_viewed",
  policyCreateStarted: "policy_create_started",
  routeCreateStarted: "route_create_started",
} as const;

export type ProductAnalyticsEventName =
  (typeof productAnalyticsEvents)[keyof typeof productAnalyticsEvents];

type AnalyticsProperties = Record<string, any>;

const pendingEmailVerificationKey = (firebaseUID: string) =>
  `pending-email-verification:${firebaseUID}`;

function sanitizeAnalyticsProperties(properties: AnalyticsProperties = {}) {
  return Object.fromEntries(
    Object.entries(properties).filter(([, value]) => value !== undefined),
  );
}

function normalizeAuthProvider(providerId?: string | null): string {
  switch (providerId) {
    case "github.com":
      return "github";
    case "google.com":
      return "google";
    case "password":
      return "password";
    default:
      return providerId || "unknown";
  }
}

function buildPageURL(pathname: string, search = "", hash = ""): string {
  if (typeof window === "undefined") {
    return `${pathname}${search}${hash}`;
  }
  return `${window.location.origin}${pathname}${search}${hash}`;
}

export function capturePageView(pathname: string, search = "", hash = "") {
  const { analytics } = getFirebase();
  const pagePath = `${pathname}${search}${hash}`;
  const pageLocation = buildPageURL(pathname, search, hash);

  if (analytics) {
    logEvent(analytics, "page_view", {
      page_location: pageLocation,
      page_path: pagePath,
    });
  }

  posthog.capture("$pageview", {
    $current_url: pageLocation,
  });
}

export function captureProductEvent(
  eventName: ProductAnalyticsEventName,
  properties: AnalyticsProperties = {},
) {
  const { analytics } = getFirebase();
  const sanitizedProperties = sanitizeAnalyticsProperties(properties);

  if (analytics) {
    logEvent(analytics, eventName, sanitizedProperties);
  }

  posthog.capture(eventName, sanitizedProperties);
}

export function captureLegacyEvent(
  name: string,
  type: string,
  user: User | undefined,
  additionalAttributes?: AnalyticsProperties,
) {
  const { analytics } = getFirebase();
  const eventID = slugify(name, {
    replacement: "_",
    lower: true,
  });

  const firebaseProperties = sanitizeAnalyticsProperties({
    name,
    pomerium_user_id: user?.id || "",
    type,
    ...additionalAttributes,
  });
  if (analytics) {
    logEvent(analytics, eventID, firebaseProperties);
  }

  posthog.capture(
    eventID,
    sanitizeAnalyticsProperties({
      type,
      ...additionalAttributes,
    }),
  );
}

export function markPendingEmailVerification(firebaseUID: string) {
  localStorage.setItem(pendingEmailVerificationKey(firebaseUID), "true");
}

export function hasPendingEmailVerification(firebaseUID: string) {
  return (
    localStorage.getItem(pendingEmailVerificationKey(firebaseUID)) === "true"
  );
}

export function clearPendingEmailVerification(firebaseUID: string) {
  localStorage.removeItem(pendingEmailVerificationKey(firebaseUID));
}

export { normalizeAuthProvider };
